import React, { useRef } from "react";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { ItemOrAbility } from "~/pages/CharacterSheet/types";
import { ROLL_TOKEN_REGEX } from "~/utils/rollUtils";
import { Button } from "~/widgets/Button/Button";
import { Badge } from "~/widgets/Badge/Badge";
import GlossaryMarkdown from "~/widgets/GlossaryMarkdown/GlossaryMarkdown";
import styles from "~/pages/CharacterSheet/widgets/ItemCard/ItemCard.scss";

export interface ItemCardProps {
  item: ItemOrAbility;
  onToggleEdit: (id: string) => void;
  onUpdate: (id: string, content: string) => void;
  onDelete: (id: string, skipConfirm?: boolean) => void;
  onMove: (id: string, direction: "up" | "down") => void;
  /** Disable drag-and-drop functionality (useful in popups/modals) */
  disableDrag?: boolean;
}

const ItemCard: React.FC<ItemCardProps> = ({
  item,
  onToggleEdit,
  onUpdate,
  onDelete,
  onMove,
  disableDrag = false,
}) => {
  const itemCardRef = useRef<HTMLDivElement>(null);

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: item.id,
    disabled: item.isEditing || disableDrag,
  });

  const style = {
    transform: isDragging ? `${CSS.Translate.toString(transform)} scale(1)` : CSS.Translate.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 100 : "auto",
  };

  const setCombinedRef = (element: HTMLDivElement | null) => {
    (itemCardRef as React.MutableRefObject<HTMLDivElement | null>).current = element;
    setNodeRef(element);
  };

  const lines = item.content.split('\n');
  const firstLine = lines[0] || "";
  const rest = lines.slice(1).join('\n');

  const isItem = firstLine.trim().match(/^\[Item\]/i);
  const isAbility = firstLine.trim().match(/^\[Ability\]/i);
  let titleText = firstLine
    .replace(/^\s*\[Item\]/i, "")
    .replace(/^\s*\[Ability\]/i, "")
    .trim();

  const itemName = (titleText.replace(ROLL_TOKEN_REGEX, "").trim()) || (isItem ? "Item" : isAbility ? "Ability" : "Unknown");

  const sourceType = isItem ? "Item" : isAbility ? "Ability" : undefined;

  if (item.isEditing) {
    const lineCount = item.content.split('\n').length;
    return (
      <div
        ref={setCombinedRef}
        style={style}
        key={item.id}
        tabIndex={0}
        className={`${styles.itemCard} ${styles.editing}`}
      >
        <textarea
          key={item.id + "-textarea"}
          className={styles.itemTextarea}
          value={item.content}
          rows={Math.max(5, lineCount + 3)}
          onChange={(e) => onUpdate(item.id, e.target.value)}
          onKeyDown={(e) => {
            // Always stop propagation to prevent parent handlers from interfering
            e.stopPropagation();
            if ((e.key === "Enter" && (e.metaKey || e.ctrlKey)) || e.key === "Escape") {
              e.preventDefault();
              onToggleEdit(item.id);
              itemCardRef.current?.focus();
            }
          }}
          placeholder="Start with [Item] or [Ability] to tag. Markdown supported..."
          autoFocus
          onFocus={(e) => {
            const len = e.target.value.length;
            e.target.setSelectionRange(len, len);
          }}
          onClick={(e) => e.stopPropagation()}
        />
        <div className={styles.editHint}>
          <strong>Enter</strong> adds a newline • <strong>CMD/CTRL+Enter</strong> or <strong>ESC</strong> to save and exit
        </div>
        <div className={styles.itemActions}>
          <Button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onToggleEdit(item.id);
            }}
            variant="primary"
            size="md"
          >
            Done
          </Button>
          <Button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onDelete(item.id, e.shiftKey);
            }}
            variant="danger"
            size="md"
          >
            Delete
          </Button>
        </div>
      </div>
    );
  }



  return (
    <div
      ref={setCombinedRef}
      style={style}
      className={`${styles.itemCard} ${isDragging ? styles.dragging : ""}`}
      {...(disableDrag ? {} : attributes)}
      {...(disableDrag ? {} : listeners)}
      tabIndex={0}
      onDoubleClick={() => onToggleEdit(item.id)}
      onKeyDown={(e) => {
        // 1. High priority: Edit on Enter (intercept before DnD kit)
        if (e.key === "Enter") {
          e.preventDefault();
          onToggleEdit(item.id);
          return;
        }

        // 2. High priority: Move on Alt + Arrows
        if (e.altKey && (e.key === "ArrowUp" || e.key === "ArrowDown")) {
          e.preventDefault();
          e.stopPropagation();
          onMove(item.id, e.key === "ArrowUp" ? "up" : "down");
          return;
        }

        // 3. Navigation between items (only when not actively dragging)
        if (!isDragging && (e.key === "ArrowUp" || e.key === "ArrowDown")) {
          const parent = itemCardRef.current?.parentElement;
          if (parent) {
            const cards = Array.from(parent.children) as HTMLElement[];
            const index = cards.indexOf(itemCardRef.current!);
            if (index !== -1) {
              const targetIndex = e.key === "ArrowUp" ? index - 1 : index + 1;
              if (targetIndex >= 0 && targetIndex < cards.length) {
                e.preventDefault();
                cards[targetIndex].focus();
                return;
              }
            }
          }
        }

        // 4. DnD kit behavior (Space to drag, Arrows when dragging, etc.)
        listeners?.onKeyDown?.(e);
      }}
      key={item.id}
    >
      <div className={styles.itemMarkdown}>
        {item.content ? (
          <>
            <div className={styles.itemHeader}>
              <span className={styles.itemTitle}>
                {isItem && <Badge variant="item" className="mb-0">Item</Badge>}
                {isAbility && <Badge variant="ability" className="mb-0">Ability</Badge>}
                {!titleText.startsWith('[') && (isItem || isAbility) && "\u00A0"} {/* Non-breaking space if the title does not start with a tag. */}
                {titleText && (
                  <GlossaryMarkdown itemContext={{ itemName, sourceType }} recognitionMode="primary" matchPlurality>
                    {titleText}
                  </GlossaryMarkdown>
                )}
              </span>
            </div>
            {rest.trim() && (
              <GlossaryMarkdown itemContext={{ itemName, sourceType }} recognitionMode="primary" matchPlurality>
                {rest}
              </GlossaryMarkdown>
            )}
          </>
        ) : (
          <span className={styles.emptyItem}>
            Empty item - Press <strong>Enter</strong> to edit
          </span>
        )}

      </div>
    </div>
  );
};

export default ItemCard;
